
import { AIReview, Approval } from "../../../foundation/api/model";
import AIReviewDot from "../../../foundation/api/model/AIReview/AIReviewDot";
import AIReviewScreen from "../../../foundation/api/model/AIReview/AIReviewScreen";
import { ApprovalPendingDotData } from "../../../foundation/api/model/dots/ApprovalPendingDot";
import Screen from "../../../foundation/model/Screen";
import VariantGroup from "../../../foundation/model/VariantGroup";
import { barrelPrefs } from "../../../foundation/prefs";
import uniquifyArrayOfObjects from "../../../foundation/utils/uniquify";
import BarrelHelpers from "../barrel/BarrelHelpers";
import fluxRuntime from "../fluxRuntime";

function getApproval({ pid, apid }: {
    pid?: string | null;
    apid?: string | null;
}): Approval | undefined {
    const projectId = pid ?? fluxRuntime.BarrelStore.getState().barrelId;

    if (!projectId || !apid) {
        return undefined;
    }

    const { projectApprovalsRecord } = fluxRuntime.ApprovalsDataStore.getState();

    return projectApprovalsRecord[projectId]?.[apid];
}

interface ScreenWithVariants extends Screen {
    variantGroup?: VariantGroup;
    variantsCount?: number;
    firstVariant?: Screen;
}

function getSortedApprovalScreens(apid: string | null): ScreenWithVariants[] {
    const pid = fluxRuntime.BarrelStore.getState().barrelId;
    const { aiReviewMode } = fluxRuntime.InspectableViewStore.getState();
    const sids = aiReviewMode
        ? (getAIReview(pid, apid)?.screens ?? []).map(({ id }) => id)
        : getApproval({ pid, apid })?.screens ?? [];
    const screensToRender: ScreenWithVariants[] = [];

    if (!sids?.length) {
        return [];
    }

    const { screens = [] } = BarrelHelpers.getProject({ pid }) ?? {};
    const projectScreens = new Map((screens as Screen[]).map(projectScreen => (
        [projectScreen._id, projectScreen]
    )));
    const screensMap = new Map<string, Screen[]>();

    sids?.forEach(id => {
        const correspondingScreen = projectScreens.get(id)!;
        const variantGroup = BarrelHelpers.getVariantGroupOfScreen(id);

        // Skip the non-existing screen
        if (!correspondingScreen) {
            return;
        }

        if (!variantGroup) {
            const nonVariants = screensMap.get("nonVariants") ?? [];

            nonVariants.push(correspondingScreen);
            screensMap.set("nonVariants", nonVariants);

            // screensToRender.push(correspondingScreen!);
        } else {
            const correspondingVariants = screensMap.get(variantGroup._id) ?? [];

            correspondingVariants.push(correspondingScreen);
            screensMap.set(variantGroup._id, correspondingVariants);
        }
    });

    sids?.forEach(id => {
        const correspondingScreen = projectScreens.get(id)!;
        const variantGroup = BarrelHelpers.getVariantGroupOfScreen(id);

        // Skip the non-existing screen
        if (!correspondingScreen) {
            return;
        }

        // if it's already pushed, go to next iteration.
        if (screensToRender.find(({ _id }) => _id === id)) {
            return;
        }

        if (!variantGroup) {
            screensToRender.push(correspondingScreen!);
        } else {
            const correspondingVariantScreens = screensMap.get(variantGroup._id)!;

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            screensToRender.push({
                name: variantGroup.name,
                _id: variantGroup._id,
                variantsCount: correspondingVariantScreens.length,
                firstVariant: correspondingScreen
            });

            correspondingVariantScreens.forEach(variantScreen => {
                screensToRender.push({
                    ...variantScreen,
                    variantGroup
                });
            });
        }
    });

    return uniquifyArrayOfObjects(screensToRender.filter(Boolean), "_id");
}

function getScreenApprovals({ pid, sid }: { pid: string | null; sid?: string; }): Approval[] {
    const projectId = pid ?? fluxRuntime.BarrelStore.getState().barrelId;
    const screenId = sid ?? fluxRuntime.InspectableViewStore.getState().inspectableId;

    if (!projectId || !screenId) {
        return [];
    }

    const { projectApprovalsRecord } = fluxRuntime.ApprovalsDataStore.getState();

    return Object.values(projectApprovalsRecord[projectId] || {})
        .filter(approval => approval.screens.includes(screenId));
}

async function getApprovalPendingDots(
    { pid, sid, apid }: { pid?: string; sid?: string; apid: string; }
): Promise<ApprovalPendingDotData[]> {
    const projectId = pid ?? fluxRuntime.BarrelStore.getState().barrelId;

    if (!projectId || !apid) {
        return [];
    }

    const { aiReviewMode } = fluxRuntime.InspectableViewStore.getState();

    const approvalSids = aiReviewMode
        ? (getAIReview(projectId, apid)?.screens ?? []).map(({ id: screenId }) => screenId)
        : getApproval({ pid: projectId, apid })!.screens;
    const projectPrefs = await barrelPrefs.getAll(pid);
    let approvalPendingDotsInProject: ApprovalPendingDotData[] = [];

    approvalSids?.forEach(apSid => {
        const screenDots = (projectPrefs[apSid]?.approvalPendingDots as ApprovalPendingDotData[] | undefined);
        const approvalPendingDotsInScreen = screenDots?.filter(apDot => apDot.approvalsId === apid);

        if (!approvalPendingDotsInScreen?.length) {
            return;
        }

        approvalPendingDotsInProject.push(...approvalPendingDotsInScreen);
    });

    // if sid is provided, return only relative screens' approvalPending dots.
    if (sid) {
        approvalPendingDotsInProject = approvalPendingDotsInProject
            .filter(({ screenId }) => screenId === sid);
    }

    return uniquifyArrayOfObjects(approvalPendingDotsInProject, "_id");
}

function getApprovalPendingDotCount({ pid, apid }: {
    pid?: string; apid: string;
}): number {
    const projectId = pid ?? fluxRuntime.BarrelStore.getState().barrelId!;
    const { approvalPendingDots } = fluxRuntime.ApprovalDotsDataStore.getState();

    return (Object.values(approvalPendingDots?.[projectId] || {}).flat() as ApprovalPendingDotData[])
        .filter(({ approvalsId }) => approvalsId === apid).length;
}

function getAIReview(
    pid?: string | null,
    apid?: string | null
): AIReview | undefined {
    const projectId = pid ?? fluxRuntime.BarrelStore.getState().barrelId;
    const aiReviewId = apid ?? fluxRuntime.InspectableViewStore.getState().approvalsId;

    if (!projectId || !aiReviewId) {
        return undefined;
    }

    const { screens: projectScreens = [] } = BarrelHelpers.getProject({ pid }) ?? {};
    const { aiReviewsRecord } = fluxRuntime.ApprovalsDataStore.getState();
    const aiReviewRaw = aiReviewsRecord?.[projectId]?.[aiReviewId];

    if (!aiReviewRaw) {
        return undefined;
    }

    return {
        ...aiReviewRaw,
        screens: aiReviewRaw?.screens
            ?.filter(({ id }) => projectScreens.some(({ _id }) => _id === id))
    };
}

function getAIReviewScreen({
    pid,
    airid,
    sid
}: {
    pid?: string | null;
    airid?: string | null;
    sid?: string | null;
} = {}): AIReviewScreen | undefined {
    const aiReview = getAIReview(pid, airid);

    if (!aiReview) {
        return undefined;
    }

    const screenId = sid ?? fluxRuntime.InspectableViewStore.getState().inspectableId!;

    return aiReview?.screens?.find(({ id }) => id === screenId);
}

function getAIReviewDot({
    pid,
    airid,
    sid,
    did
}: {
    pid?: string | null;
    airid?: string | null;
    sid?: string | null;
    did?: string;
} = {}): AIReviewDot | undefined {
    const aiReviewScreen = getAIReviewScreen({ pid, airid, sid });

    if (!aiReviewScreen || !did) {
        return undefined;
    }

    return aiReviewScreen?.dots?.find(dot => dot._id === did);
}

function getProjectAIReviews(pid?: string): AIReview[] | undefined {
    const projectId = pid ?? fluxRuntime.BarrelStore.getState().barrelId;

    if (!projectId) {
        return undefined;
    }

    const { aiReviewsRecord } = fluxRuntime.ApprovalsDataStore.getState();

    if (!aiReviewsRecord || !aiReviewsRecord[projectId]) {
        return undefined;
    }

    const { screens: projectScreens = [] } = BarrelHelpers.getProject({ pid }) ?? {};
    const rawAIReviews = Object.values(aiReviewsRecord[projectId] || {});

    return rawAIReviews.map(aiReviewRaw => ({
        ...aiReviewRaw,
        screens: aiReviewRaw?.screens
            .filter(({ id }) => projectScreens.some(({ _id }) => _id === id))
    }));
}

interface AIReviewScreenWithVariants extends AIReviewScreen {
    variantGroup?: VariantGroup;
    variantsCount?: number;
    firstVariant?: AIReviewScreen;
}

function getSortedAIReviewScreens(apid: string | null): AIReviewScreenWithVariants[] {
    const pid = fluxRuntime.BarrelStore.getState().barrelId;
    const aiReview = getAIReview(pid, apid);

    if (aiReview === undefined) {
        return [];
    }

    const sids = aiReview.screens?.map(({ id }) => id);
    const screensToRender: AIReviewScreenWithVariants[] = [];

    if (!sids?.length) {
        return [];
    }

    const aiReviewScreens = new Map(aiReview.screens.map(aiReviewScreen => (
        [aiReviewScreen.id, aiReviewScreen]
    )));
    const screensMap = new Map<string, AIReviewScreen[]>();

    sids?.forEach(sid => {
        const correspondingScreen = aiReviewScreens.get(sid)!;
        const variantGroup = BarrelHelpers.getVariantGroupOfScreen(sid);

        // Skip the non-existing screen
        if (!correspondingScreen) {
            return;
        }

        if (!variantGroup) {
            const nonVariants = screensMap.get("nonVariants") ?? [];

            nonVariants.push(correspondingScreen);
            screensMap.set("nonVariants", nonVariants);
        } else {
            const correspondingVariants = screensMap.get(variantGroup._id) ?? [];

            correspondingVariants.push(correspondingScreen);
            screensMap.set(variantGroup._id, correspondingVariants);
        }
    });

    sids?.forEach(sid => {
        const correspondingScreen = aiReviewScreens.get(sid)!;
        const variantGroup = BarrelHelpers.getVariantGroupOfScreen(sid);

        // Skip the non-existing screen
        if (!correspondingScreen) {
            return;
        }

        // if it's already pushed, go to next iteration.
        if (screensToRender.find(({ id }) => id === sid)) {
            return;
        }

        if (!variantGroup) {
            screensToRender.push(correspondingScreen!);
        } else {
            const correspondingVariantScreens = screensMap.get(variantGroup._id)!;

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            screensToRender.push({
                name: variantGroup.name,
                id: variantGroup._id,
                variantsCount: correspondingVariantScreens.length,
                firstVariant: correspondingScreen
            });

            correspondingVariantScreens.forEach(variantScreen => {
                screensToRender.push({
                    ...variantScreen,
                    variantGroup
                });
            });
        }
    });

    return uniquifyArrayOfObjects(screensToRender.filter(Boolean), "id");
}

export type { AIReviewScreenWithVariants };

export default {
    getApproval,
    getAIReview,
    getAIReviewScreen,
    getAIReviewDot,
    getProjectAIReviews,
    getSortedApprovalScreens,
    getScreenApprovals,
    getApprovalPendingDots,
    getApprovalPendingDotCount,
    getSortedAIReviewScreens
};
